import React, {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
  useCallback,
} from "react";
import { logConsentChoice } from "../services/consentService";
import { useAuth } from "./AuthContext";

const COOKIE_PREF_KEY = "cookie_preferences";
const COOKIE_ACCEPTED_KEY = "cookies_accepted";

const defaultPreferences = {
  essential: true,
  functional: false,
};

const CookieContext = createContext(null);

export function CookieProvider({ children }) {
  const [preferences, setPreferences] = useState(defaultPreferences);
  const [hasSavedPreferences, setHasSavedPreferences] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const { currentUser } = useAuth();

  useEffect(() => {
    if (typeof window === "undefined") return;
    try {
      const stored = localStorage.getItem(COOKIE_PREF_KEY);
      if (stored) {
        const parsed = JSON.parse(stored);
        setPreferences({ ...defaultPreferences, ...parsed, essential: true });
        setHasSavedPreferences(true);
      }
    } catch (error) {
      console.warn("Unable to read cookie preferences", error);
    }
  }, []);

  const persistPreferences = useCallback((prefs, source = "cookie_banner") => {
    if (typeof window === "undefined") return;
    const payload = { ...defaultPreferences, ...prefs, essential: true };
    localStorage.setItem(COOKIE_PREF_KEY, JSON.stringify(payload));
    localStorage.setItem(COOKIE_ACCEPTED_KEY, payload.functional ? "true" : "false");
    setPreferences(payload);
    setHasSavedPreferences(true);

    // Log consent choice to Firestore for audit trail (GDPR/CNIL compliance)
    logConsentChoice(
      payload,
      currentUser?.uid || null,
      typeof navigator !== "undefined" ? navigator.userAgent : null
    ).catch((error) => {
      // Non-blocking: consent is saved locally even if logging fails
      console.warn("Failed to log consent to audit trail:", error);
    });
  }, [currentUser]);

  const acceptAll = useCallback(() => {
    persistPreferences({ functional: true }, "cookie_banner");
    setIsModalOpen(false);
  }, [persistPreferences]);

  const rejectAll = useCallback(() => {
    persistPreferences({ functional: false }, "cookie_banner");
    setIsModalOpen(false);
  }, [persistPreferences]);

  const updatePreference = useCallback((key, value) => {
    if (key === "essential") return; // essential is always on
    setPreferences((prev) => ({ ...prev, [key]: value }));
  }, []);

  const savePreferences = useCallback(() => {
    persistPreferences(preferences, "modal");
    setIsModalOpen(false);
  }, [preferences, persistPreferences]);

  const openPreferences = useCallback(() => setIsModalOpen(true), []);
  const closePreferences = useCallback(() => setIsModalOpen(false), []);

  const value = useMemo(
    () => ({
      preferences,
      hasSavedPreferences,
      isModalOpen,
      acceptAll,
      rejectAll,
      updatePreference,
      savePreferences,
      openPreferences,
      closePreferences,
      canUseFunctionalCookies: preferences.functional && hasSavedPreferences,
      showBanner: !hasSavedPreferences && !isModalOpen,
    }),
    [
      preferences,
      hasSavedPreferences,
      isModalOpen,
      acceptAll,
      rejectAll,
      updatePreference,
      savePreferences,
      openPreferences,
      closePreferences,
    ]
  );

  return <CookieContext.Provider value={value}>{children}</CookieContext.Provider>;
}

export function useCookiePreferences() {
  const ctx = useContext(CookieContext);
  if (!ctx) throw new Error("useCookiePreferences must be used within CookieProvider");
  return ctx;
}

